import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import useApi from '../utils/api';
import { ROLE_ADMIN, ROLE_CREATOR } from '../utils/roles';

const getMemberLabel = (member) => member.name || member.email || member.public_user_id;

const buildTransfersPayload = (ownership, orgSuccessorPublicUserId, gamespaceSuccessors, gameCreatorSuccessors) => {
  const needsOrgTransfer = Boolean(ownership?.is_organization_owner);
  const ownedGamespaces = ownership?.owned_gamespaces || [];
  const createdGamesByGamespace = ownership?.created_games_by_gamespace || [];
  const requiresTransfers =
    needsOrgTransfer || ownedGamespaces.length > 0 || createdGamesByGamespace.length > 0;

  if (!requiresTransfers) {
    return undefined;
  }

  return {
    ...(needsOrgTransfer
      ? { organization_new_owner_public_user_id: orgSuccessorPublicUserId }
      : {}),
    gamespace_transfers: ownedGamespaces.map((space) => ({
      space_id: space.space_id,
      new_owner_public_user_id: gamespaceSuccessors[space.space_id],
    })),
    game_creator_transfers: createdGamesByGamespace.map((group) => ({
      gamespace_id: group.gamespace_id,
      new_creator_public_user_id: gameCreatorSuccessors[group.gamespace_id],
    })),
  };
};

const validateDeleteForm = ({
  ownership,
  orgSuccessorPublicUserId,
  gamespaceSuccessors,
  gameCreatorSuccessors,
  variant,
  userEmail,
  emailConfirmation,
}) => {
  if (ownership?.has_active_subscription) {
    return 'Cancel or transfer the user subscription before deleting this account.';
  }

  if (ownership?.is_sole_org_owner) {
    return (
      'You are the only organization member. Invite another member and transfer organization ' +
      'ownership before deleting your account.'
    );
  }

  const ownedGamespaces = ownership?.owned_gamespaces || [];
  const createdGamesByGamespace = ownership?.created_games_by_gamespace || [];

  if (ownership?.is_organization_owner && !orgSuccessorPublicUserId) {
    return 'Select a new organization owner.';
  }

  for (const space of ownedGamespaces) {
    if (!gamespaceSuccessors[space.space_id]) {
      return `Select a new owner for Learnspace "${space.space_name}".`;
    }
  }

  for (const group of createdGamesByGamespace) {
    if (!gameCreatorSuccessors[group.gamespace_id]) {
      return `Select a new game creator for ${group.games.length} game(s) in "${group.gamespace_name}".`;
    }
  }

  if (variant === 'self') {
    const normalizedEmail = (userEmail || '').trim().toLowerCase();
    const normalizedConfirmation = (emailConfirmation || '').trim().toLowerCase();
    if (!normalizedEmail || normalizedConfirmation !== normalizedEmail) {
      return 'Type your email address to confirm account deletion.';
    }
  }

  return '';
};

export default function DeleteAccountDialog({
  open,
  onClose,
  variant = 'admin',
  deleteTarget = null,
  userEmail = '',
  members = [],
  onSuccess,
}) {
  const { get, del } = useApi();
  const [ownership, setOwnership] = useState(null);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orgSuccessorPublicUserId, setOrgSuccessorPublicUserId] = useState('');
  const [gamespaceSuccessors, setGamespaceSuccessors] = useState({});
  const [gameCreatorSuccessors, setGameCreatorSuccessors] = useState({});
  const [emailConfirmation, setEmailConfirmation] = useState('');

  const excludeEmail = variant === 'admin' ? deleteTarget?.email : userEmail;
  const excludePublicUserId =
    variant === 'self' ? ownership?.public_user_id : deleteTarget?.public_user_id;
  const normalizedExcludeEmail = (excludeEmail || '').trim().toLowerCase();

  const filterSuccessorCandidates = (candidates) =>
    (candidates || []).filter((member) => {
      if (excludePublicUserId && member.public_user_id === excludePublicUserId) {
        return false;
      }
      const memberEmail = (member.email || '').trim().toLowerCase();
      return !normalizedExcludeEmail || memberEmail !== normalizedExcludeEmail;
    });

  const getOrganizationSuccessorOptions = () =>
    filterSuccessorCandidates(
      ownership?.organization_successor_candidates?.length
        ? ownership.organization_successor_candidates
        : members
    );

  const getLearnspaceSuccessorOptions = (space) =>
    filterSuccessorCandidates(
      space?.successor_candidates?.length ? space.successor_candidates : members
    );

  const getGameCreatorSuccessorOptionsForGroup = (group) =>
    filterSuccessorCandidates(
      group?.successor_candidates?.length
        ? group.successor_candidates
        : members.filter(
            (member) =>
              member.role_id === ROLE_ADMIN || member.role_id === ROLE_CREATOR
          )
    );

  const resetState = () => {
    setOwnership(null);
    setOwnershipLoading(false);
    setError('');
    setSubmitting(false);
    setOrgSuccessorPublicUserId('');
    setGamespaceSuccessors({});
    setGameCreatorSuccessors({});
    setEmailConfirmation('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    const loadOwnership = async () => {
      setOwnershipLoading(true);
      setError('');
      setOrgSuccessorPublicUserId('');
      setGamespaceSuccessors({});
      setGameCreatorSuccessors({});
      setEmailConfirmation('');

      try {
        const ownershipUrl =
          variant === 'self'
            ? `${process.env.REACT_APP_API_URL}/auth/account/ownership`
            : `${process.env.REACT_APP_API_URL}/auth/organization/members/ownership?email=${encodeURIComponent(deleteTarget.email)}`;
        const summary = await get(ownershipUrl);
        if (cancelled) return;

        setOwnership(summary);
        const initialGamespaceSuccessors = {};
        (summary?.owned_gamespaces || []).forEach((space) => {
          initialGamespaceSuccessors[space.space_id] = '';
        });
        setGamespaceSuccessors(initialGamespaceSuccessors);

        const initialGameCreatorSuccessors = {};
        (summary?.created_games_by_gamespace || []).forEach((group) => {
          initialGameCreatorSuccessors[group.gamespace_id] = '';
        });
        setGameCreatorSuccessors(initialGameCreatorSuccessors);
      } catch (loadError) {
        if (cancelled) return;
        console.error('Failed to load ownership summary:', loadError);
        setError(
          loadError?.response?.data?.error ||
            loadError?.message ||
            'Could not load ownership information for this account.'
        );
      } finally {
        if (!cancelled) {
          setOwnershipLoading(false);
        }
      }
    };

    loadOwnership();

    return () => {
      cancelled = true;
    };
  }, [open, variant, deleteTarget?.email, get]);

  const handleConfirmDelete = async () => {
    const validationError = validateDeleteForm({
      ownership,
      orgSuccessorPublicUserId,
      gamespaceSuccessors,
      gameCreatorSuccessors,
      variant,
      userEmail,
      emailConfirmation,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    const transfers = buildTransfersPayload(
      ownership,
      orgSuccessorPublicUserId,
      gamespaceSuccessors,
      gameCreatorSuccessors
    );

    setSubmitting(true);
    setError('');

    try {
      if (variant === 'self') {
        const url = `${process.env.REACT_APP_API_URL}/auth/account`;
        await del(url, transfers ? { transfers } : {});
      } else {
        const url = `${process.env.REACT_APP_API_URL}/auth/organization/delete-user`;
        await del(url, {
          email: deleteTarget.email,
          ...(transfers ? { transfers } : {}),
        });
      }

      onSuccess?.();
      handleClose();
    } catch (deleteError) {
      console.error('Failed to delete user account:', deleteError);
      setError(
        deleteError?.response?.data?.error ||
          deleteError?.message ||
          'Could not delete the user account.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title = variant === 'self' ? 'Delete my account' : 'Delete user account';
  const targetLabel = variant === 'self' ? userEmail : deleteTarget?.email;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {targetLabel ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {variant === 'self' ? (
              <>
                Permanently remove your account <strong>{targetLabel}</strong> from this
                organization?
              </>
            ) : (
              <>
                Permanently remove <strong>{targetLabel}</strong> from this organization?
              </>
            )}
          </Typography>
        ) : null}

        {ownershipLoading ? (
          <Typography variant="body2">Loading ownership details…</Typography>
        ) : null}

        {!ownershipLoading && ownership?.has_active_subscription ? (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {variant === 'self'
              ? 'You have an active subscription. Cancel billing before deleting your account.'
              : 'This user has an active subscription. Cancel or transfer billing before deletion.'}
          </Typography>
        ) : null}

        {!ownershipLoading && ownership?.is_sole_org_owner ? (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            You are the only organization member. Invite another member and transfer organization
            ownership before deleting your account.
          </Typography>
        ) : null}

        {!ownershipLoading && ownership?.is_organization_owner && !ownership?.is_sole_org_owner ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              New organization owner
            </Typography>
            <Select
              fullWidth
              displayEmpty
              value={orgSuccessorPublicUserId}
              onChange={(e) => setOrgSuccessorPublicUserId(e.target.value)}
            >
              <MenuItem value="" disabled>
                Select member
              </MenuItem>
              {getOrganizationSuccessorOptions().map((member) => (
                <MenuItem key={member.public_user_id} value={member.public_user_id}>
                  {getMemberLabel(member)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : null}

        {!ownershipLoading &&
          (ownership?.owned_gamespaces || []).map((space) => (
            <Box key={space.space_id} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                New owner for {space.space_name}
              </Typography>
              <Select
                fullWidth
                displayEmpty
                value={gamespaceSuccessors[space.space_id] || ''}
                onChange={(e) =>
                  setGamespaceSuccessors((prev) => ({
                    ...prev,
                    [space.space_id]: e.target.value,
                  }))
                }
              >
                <MenuItem value="" disabled>
                  Select member
                </MenuItem>
                {getLearnspaceSuccessorOptions(space).map((member) => (
                  <MenuItem key={member.public_user_id} value={member.public_user_id}>
                    {getMemberLabel(member)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}

        {!ownershipLoading &&
          (ownership?.created_games_by_gamespace || []).map((group) => (
            <Box key={`creator-${group.gamespace_id}`} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                New game creator for {group.games.length} game(s) in {group.gamespace_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Successor must have admin or creator role.
              </Typography>
              <Select
                fullWidth
                displayEmpty
                value={gameCreatorSuccessors[group.gamespace_id] || ''}
                onChange={(e) =>
                  setGameCreatorSuccessors((prev) => ({
                    ...prev,
                    [group.gamespace_id]: e.target.value,
                  }))
                }
              >
                <MenuItem value="" disabled>
                  Select member
                </MenuItem>
                {getGameCreatorSuccessorOptionsForGroup(group).map((member) => (
                  <MenuItem key={member.public_user_id} value={member.public_user_id}>
                    {getMemberLabel(member)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ))}

        {variant === 'self' && !ownershipLoading ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Confirm your email
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={userEmail || 'your@email.com'}
              value={emailConfirmation}
              onChange={(e) => setEmailConfirmation(e.target.value)}
              autoComplete="off"
            />
          </Box>
        ) : null}

        {error ? (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDelete}
          color="error"
          variant="contained"
          disabled={
            submitting ||
            ownershipLoading ||
            ownership?.is_sole_org_owner ||
            ownership?.has_active_subscription
          }
        >
          {variant === 'self' ? 'Delete my account' : 'Delete account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
